import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import debounce from 'debounce-promise';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { commonProps, customStyles } from './CustomComponents';
import { OptionMarkedLabel } from './OptionMarkedLabel';
import { SAsyncCreatableSelect, StyledSelect, SCreatableSelect, SAsyncSelect } from './styled';
import { useFixedOptions } from './useFixedOptions';
import { useMergeComponents } from './useMergeComponents';
export { default as TagInput } from './TagInput';
const mergeStyles = (styles) => ({
    ...customStyles,
    ...styles,
});
// the `ref` should be passed using `innerRef` custom prop & not with `forwardRef` because typescript
// currently doesn't support hoc generic types inference well. therefore it's not possible to create a
// generic component using `forwardRef`
//
// const customFilter is used to support EN/DE localization
export const Select = ({ components, innerRef, formatOptionLabel, styles, ...props }) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const customFilter = React.useCallback((option, searchText) => {
        const target = formatOptionLabel?.(option, {
            context: 'value',
            inputValue: '',
            selectValue: [option],
        })?.toString() ?? option.label;
        if (typeof target !== 'string') {
            // eslint-disable-next-line no-console
            console.error('Ether value or label be be string');
            return false;
        }
        return target.toLowerCase().includes(searchText.toLowerCase());
    }, [formatOptionLabel]);
    const mergedComponents = useMergeComponents(components);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(StyledSelect, { styles: mergedStyles, filterOption: customFilter, ref: innerRef, formatOptionLabel: formatOptionLabel, ...commonProps, ...props, components: mergedComponents, "data-testid": 'styled-select' }));
};
export const StyledAsyncCreatableSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncCreatableSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => (loadOptions ? debounce(loadOptions, 300) : undefined), [loadOptions]), ...commonProps, ...props, components: mergedComponents }));
};
export const AsyncCreatableMultiSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncCreatableSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => (loadOptions ? debounce(loadOptions, 300) : undefined), [loadOptions]), ...commonProps, ...props, isMulti: true, components: mergedComponents }));
};
export const StyledCreatableSelect = ({ components, innerRef, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    return (_jsx(SCreatableSelect, { styles: customStyles, ref: innerRef, ...commonProps, ...props, components: mergedComponents }));
};
export const CreatableMultiSelect = ({ components, innerRef, ...restProps }) => {
    const mergedComponents = useMergeComponents(components);
    const fixed = useFixedOptions(restProps);
    return (_jsx(SCreatableSelect, { styles: customStyles, ref: innerRef, ...commonProps, ...restProps, ...fixed, isMulti: true, components: mergedComponents }));
};
export const AsyncSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => (loadOptions ? debounce(loadOptions, 300) : undefined), [loadOptions]), defaultOptions: true, ...commonProps, ...props, components: mergedComponents }));
};
const multiProps = {
    isMulti: true,
    closeMenuOnSelect: false,
};
export const MultiSelect = ({ components, innerRef, formatOptionLabel, filterOption, ...restProps }) => {
    const mergedComponents = useMergeComponents(components);
    const fixed = useFixedOptions(restProps);
    // Auto-generate customFilter for better search when formatOptionLabel is provided (same pattern as Select component)
    const customFilter = useCallbackRef((option, searchText) => {
        if (!formatOptionLabel)
            return true;
        const target = formatOptionLabel(option, {
            context: 'value',
            inputValue: '',
            selectValue: [option],
        })?.toString() ?? option.label;
        if (typeof target !== 'string') {
            return false;
        }
        return target.toLowerCase().includes(searchText.toLowerCase());
    });
    return (_jsx(StyledSelect, { styles: customStyles, ref: innerRef, formatOptionLabel: formatOptionLabel, filterOption: filterOption || (formatOptionLabel ? customFilter : undefined), ...multiProps, ...commonProps, ...restProps, ...fixed, components: mergedComponents }));
};
export const AsyncMultiSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => (loadOptions ? debounce(loadOptions, 300) : undefined), [loadOptions]), defaultOptions: true, ...multiProps, ...commonProps, ...props, components: mergedComponents }));
};
export { OptionMarkedLabel };
export { CustomOption } from './CustomComponents';
